// src/pages/Auth.jsx
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function Auth() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Login Form
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
  });

  // Register Form
  const [registerForm, setRegisterForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const handleLoginChange = (e) => {
    setLoginForm({ ...loginForm, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const handleRegisterChange = (e) => {
    setRegisterForm({ ...registerForm, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 800));

      if (loginForm.email && loginForm.password) {
        login("Admin");           // Use context login
        navigate("/");            // Redirect to dashboard
      } else {
        throw new Error("Please fill all fields");
      }
    } catch (err) {
      setError(err.message || "Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    if (!registerForm.name.trim()) return setError("Full name is required");
    if (!registerForm.email.includes("@")) return setError("Valid email is required");
    if (registerForm.password.length < 6) return setError("Password must be at least 6 characters");
    if (registerForm.password !== registerForm.confirmPassword) return setError("Passwords do not match");

    setLoading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // For now, after register we switch to login tab
      setError(""); 
      alert("Registration successful! Please sign in.");
      setActiveTab("login");
      setRegisterForm({ name: "", email: "", password: "", confirmPassword: "" });

    } catch (err) {
      setError("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-black flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-10">
          <span className="text-4xl font-bold bg-gradient-to-r from-accent-orange-400 to-accent-orange-600 bg-clip-text text-transparent">
            ONE DESK
          </span>
          <h1 className="text-3xl font-bold text-white mt-4">
            {activeTab === "login" ? "Welcome Back" : "Create Account"}
          </h1>
          <p className="text-secondary-text mt-2">
            {activeTab === "login" 
              ? "Sign in to access your admin dashboard" 
              : "Join as a Super Admin"}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-card-border mb-8">
          <button
            onClick={() => setActiveTab("login")}
            className={`flex-1 py-3 text-sm font-medium transition-all ${
              activeTab === "login"
                ? "text-white border-b-2 border-accent-orange-500"
                : "text-secondary-text hover:text-primary-text"
            }`}
          >
            Sign In
          </button>
          <button
            onClick={() => setActiveTab("register")}
            className={`flex-1 py-3 text-sm font-medium transition-all ${
              activeTab === "register"
                ? "text-white border-b-2 border-accent-orange-500"
                : "text-secondary-text hover:text-primary-text"
            }`}
          >
            Register
          </button>
        </div>

        <div className="bg-card-bg border border-card-border rounded-3xl p-8 shadow-2xl">
          {error && (
            <div className="bg-red-500/10 border border-red-500/40 text-red-400 px-4 py-3 rounded-xl text-sm mb-6">
              {error}
            </div>
          )}

          {activeTab === "login" ? (
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-secondary-text mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={loginForm.email}
                  onChange={handleLoginChange}
                  required
                  className="w-full px-5 py-4 bg-secondary-bg border border-card-border rounded-2xl focus:border-accent-orange-500 focus:ring-2 focus:ring-accent-orange-500/20 text-primary-text placeholder:text-secondary-text"
                  placeholder="admin@onedesk.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-text mb-2">Password</label>
                <input
                  type="password"
                  name="password"
                  value={loginForm.password}
                  onChange={handleLoginChange}
                  required
                  className="w-full px-5 py-4 bg-secondary-bg border border-card-border rounded-2xl focus:border-accent-orange-500 focus:ring-2 focus:ring-accent-orange-500/20 text-primary-text"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 disabled:opacity-70 rounded-2xl font-semibold text-white shadow-lg transition-all"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-secondary-text mb-2">Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={registerForm.name}
                  onChange={handleRegisterChange}
                  required
                  className="w-full px-5 py-4 bg-secondary-bg border border-card-border rounded-2xl focus:border-accent-orange-500 focus:ring-2 focus:ring-accent-orange-500/20 text-primary-text placeholder:text-secondary-text"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-text mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={registerForm.email}
                  onChange={handleRegisterChange}
                  required
                  className="w-full px-5 py-4 bg-secondary-bg border border-card-border rounded-2xl focus:border-accent-orange-500 focus:ring-2 focus:ring-accent-orange-500/20 text-primary-text placeholder:text-secondary-text"
                  placeholder="admin@onedesk.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-text mb-2">Password</label>
                <input
                  type="password"
                  name="password"
                  value={registerForm.password}
                  onChange={handleRegisterChange}
                  required
                  className="w-full px-5 py-4 bg-secondary-bg border border-card-border rounded-2xl focus:border-accent-orange-500 focus:ring-2 focus:ring-accent-orange-500/20 text-primary-text"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-text mb-2">Confirm Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={registerForm.confirmPassword}
                  onChange={handleRegisterChange}
                  required
                  className="w-full px-5 py-4 bg-secondary-bg border border-card-border rounded-2xl focus:border-accent-orange-500 focus:ring-2 focus:ring-accent-orange-500/20 text-primary-text"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 disabled:opacity-70 rounded-2xl font-semibold text-white shadow-lg transition-all"
              >
                {loading ? "Creating Account..." : "Create Super Admin Account"}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-secondary-text mt-8">
          © 2026 OneDesk CRM. All rights reserved.
        </p>
      </div>
    </div>
  );
}